import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { ExpenseService, GroupedExpense } from '../../core/services/expense.service';
import { AuthService } from '../../core/services/auth.service';
import { HabitService } from '../../core/services/habit.service';
import { UserService } from '../../core/services/user.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { DashboardActionsComponent } from './components/dashboard-actions/dashboard-actions.component';
import { DashboardChartComponent, LegendItem } from './components/dashboard-chart/dashboard-chart.component';
import { FamilyService } from '../../core/services/family.service';
import { MonthlyReportComponent } from './components/monthly-report/monthly-report.component';

import { MatRippleModule } from '@angular/material/core';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule, MatButtonModule, DashboardChartComponent, MatRippleModule, MonthlyReportComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {
  expenseService = inject(ExpenseService);
  authService = inject(AuthService);
  habitService = inject(HabitService);
  router = inject(Router);
  cd = inject(ChangeDetectorRef);
  userService = inject(UserService);
  familyService = inject(FamilyService);

  // View State (Filters)
  viewDate: Date = new Date();

  // Data State
  totalBalance = 0;
  budgetLimit = 0;

  // Computed State
  budgetPercentage = 0;
  isOverLimit = false;
  legendItems: LegendItem[] = [];
  topSpends: LegendItem[] = []; // Top 3 categories
  habits$ = this.habitService.habits$;

  // Child Component Data
  monthTotal = 0;
  monthBudget = 0;
  monthCatTotals: { [key: string]: number } = {};

  // Full Data Store
  fullExpenses: GroupedExpense[] = [];

  // Theme Colors
  colors = ['#6C5DD3', '#FF708B', '#FFBA69', '#01C0C8', '#8A76FF', '#FF8F6B', '#05CD99', '#A3AED0'];

  constructor() { }

  ngOnInit() {
    // 1. User Profile -> Context
    this.userService.currentUserProfile$.subscribe(profile => {
      if (profile) {
        this.expenseService.setContext('family', 'default');
      }
    });

    // 2. Family Budget
    this.familyService.currentFamily$.subscribe(family => {
      if (family) {
        this.budgetLimit = family.monthlyBudget || 0;
        this.monthBudget = this.budgetLimit;
        this.calculateBudgetStatus();
      }
    });

    // 3. Habits
    this.habitService.fetchHabits();

    // 4. Expenses
    this.expenseService.groupedExpenses$.subscribe(groups => {
      this.fullExpenses = groups;
      this.filterAndProcessData();
      this.cd.detectChanges();
    });
  }

  // === Month Navigation ===
  changeMonth(delta: number) {
    const newDate = new Date(this.viewDate);
    newDate.setMonth(newDate.getMonth() + delta);
    this.viewDate = newDate;
    this.filterAndProcessData();
  }

  get viewMonthLabel(): string {
    return this.viewDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  }

  get isMonthlyReportVisible(): boolean {
    const today = new Date();

    // Compare Months strictly
    const currentMonthIndex = today.getFullYear() * 12 + today.getMonth();
    const viewMonthIndex = this.viewDate.getFullYear() * 12 + this.viewDate.getMonth();

    if (viewMonthIndex < currentMonthIndex) {
      // Past month: Always show
      return true;
    } else if (viewMonthIndex === currentMonthIndex) {
      // Current month: Show only after 26th
      return today.getDate() > 26;
    }

    // Future month: Hide
    return false;
  }

  // === Data Processing ===
  filterAndProcessData() {
    const y = this.viewDate.getFullYear();
    const m = String(this.viewDate.getMonth() + 1).padStart(2, '0');
    const MonthStr = `${y}-${m}`;

    // Filter Groups
    const filteredGroups = this.fullExpenses.filter(g => g.date.startsWith(MonthStr));

    this.processData(filteredGroups);
  }

  processData(groups: GroupedExpense[]) {
    let total = 0;
    const catTotals: { [key: string]: number } = {};

    groups.forEach(group => {
      group.items.forEach(item => {
        total += item.amount;
        catTotals[item.category] = (catTotals[item.category] || 0) + item.amount;
      });
    });

    this.totalBalance = total;
    this.monthTotal = total;
    this.monthCatTotals = catTotals;

    // First pass: Calculate initial percentages
    let processedItems = Object.entries(catTotals)
      .map(([name, amount]) => ({
        name,
        amount,
        percentage: total > 0 ? (amount / total) * 100 : 0
      }))
      .sort((a, b) => b.amount - a.amount);

    // Group small categories (< 3%) into "Others"
    const threshold = 3;
    const mainItems = processedItems.filter(item => item.percentage >= threshold);
    const smallItems = processedItems.filter(item => item.percentage < threshold);

    if (smallItems.length > 0) {
      const othersAmount = smallItems.reduce((acc, curr) => acc + curr.amount, 0);
      mainItems.push({
        name: 'Others',
        amount: othersAmount,
        percentage: total > 0 ? (othersAmount / total) * 100 : 0
      });
    }

    // Assign colors and icons
    const finalItems = mainItems
      .sort((a, b) => b.amount - a.amount)
      .map((item, index) => ({
        ...item,
        color: this.colors[index % this.colors.length],
        icon: this.getIconForCategory(item.name)
      }));

    // Calculate SVG Props for Donut
    let cumulativePercent = 0;
    this.legendItems = finalItems.map(item => {
      const gap = finalItems.length > 1 ? 1 : 0;
      const effectivePercent = Math.max(0, item.percentage - gap);
      const dashArray = `${effectivePercent} ${100 - effectivePercent}`;
      const dashOffset = 25 - cumulativePercent;
      cumulativePercent += item.percentage;

      return {
        ...item,
        dashArray,
        dashOffset,
      } as LegendItem;
    });

    this.topSpends = this.legendItems.slice(0, 3);

    this.calculateBudgetStatus();
  }

  calculateBudgetStatus() {
    if (this.budgetLimit > 0) {
      this.budgetPercentage = Math.round(((this.budgetLimit - this.totalBalance) / this.budgetLimit) * 100);
      this.isOverLimit = this.totalBalance > this.budgetLimit;
    } else {
      this.budgetPercentage = 0;
    }
  }

  // === Utilities ===
  getIconForCategory(cat: string): string {
    const map: { [key: string]: string } = {
      'Food': 'restaurant',
      'Travel': 'flight',
      'Entertainment': 'movie',
      'Shopping': 'shopping_bag',
      'Education': 'school',
      'Health': 'medical_services',
      'Bills': 'receipt_long',
      'Utilities': 'bolt',
      'Rent': 'home',
      'Loan': 'account_balance',
      'Investment': 'trending_up',
      'Savings': 'savings',
      'Transport': 'directions_car',
      'Groceries': 'local_grocery_store',
      'Dining': 'restaurant_menu',
      'Others': 'more_horiz',
      'Other': 'more_horiz'
    };
    const key = Object.keys(map).find(k => cat.toLowerCase().includes(k.toLowerCase()));
    return key ? map[key] : 'category';
  }

  navigate(path: string) {
    this.router.navigate([path]);
  }

  handleBudgetAction() {
    if (this.budgetLimit > 0) {
      this.router.navigate(['/reports/family']);
    } else {
      this.router.navigate(['/family/manage']);
    }
  }
}
